import type { MouseEvent, ReactNode, RefObject } from "react";
import { useId } from "react";
import { FocusScope } from "../a11y/FocusScope";
import { Text } from "./Text";
import styles from "./Sheet.module.css";

export interface SheetProps {
  readonly open: boolean;
  readonly title: string;
  readonly description?: string;
  readonly children?: ReactNode;
  /** Escape, and the scrim if `dismissOnScrimClick` is left on. */
  readonly onDismiss: () => void;
  readonly footer?: ReactNode;
  readonly initialFocus?: RefObject<HTMLElement | null>;
  /**
   * Off for a sheet whose dismissal has consequences — a partially entered
   * effort, an unsaved draft — where a stray tap outside would discard work.
   */
  readonly dismissOnScrimClick?: boolean;
  readonly centered?: boolean;
  /**
   * `alertdialog` for a decision the user must answer — it makes assistive
   * technology read the message immediately instead of waiting to be asked.
   * Reserve it for exactly that; an ordinary sheet that steals the reading
   * order this way is the AT equivalent of a modal that steals focus.
   */
  readonly role?: "dialog" | "alertdialog";
  readonly testId?: string;
}

/**
 * A modal sheet.
 *
 * Rendered in place rather than through a portal. `createPortal` needs a DOM
 * node to render into, which means reaching for `document.body` — and
 * `packages/ui` is only permitted to touch the two globals in the §1.1
 * allowlist (`document.activeElement` and `element.focus()`). A fixed-position
 * scrim achieves the same visual result without the boundary violation; if a
 * stacking context ever traps one, that is a shell concern and it is fixed
 * where the stacking context is created.
 *
 * `aria-modal` plus the focus trap is what keeps assistive technology out of
 * the surface behind an overlay, which for the summary overlay is the *only*
 * thing keeping AT out of a still-mounted live workout (§8.5).
 */
export function Sheet({
  open,
  title,
  description,
  children,
  onDismiss,
  footer,
  initialFocus,
  dismissOnScrimClick = true,
  centered = false,
  role = "dialog",
  testId,
}: SheetProps) {
  const titleId = useId();
  const descriptionId = useId();

  if (!open) return null;

  function handleScrimClick(event: MouseEvent<HTMLDivElement>) {
    // Only a click on the scrim itself. Without the identity check a click that
    // starts inside the sheet and drifts out dismisses it.
    if (!dismissOnScrimClick) return;
    if (event.target !== event.currentTarget) return;
    onDismiss();
  }

  const hasDescription = description !== undefined && description !== "";

  return (
    <div
      className={[styles.scrim, centered ? styles.centered : undefined]
        .filter((value): value is string => Boolean(value))
        .join(" ")}
      onClick={handleScrimClick}
      data-testid={testId === undefined ? undefined : `${testId}.scrim`}
    >
      <FocusScope
        role={role}
        ariaLabelledBy={titleId}
        ariaDescribedBy={hasDescription ? descriptionId : undefined}
        onDismiss={onDismiss}
        initialFocus={initialFocus}
        className={styles.sheet}
        testId={testId}
      >
        <div className={styles.header}>
          <Text as="h2" id={titleId} variant="title3" weight="semibold">
            {title}
          </Text>
          {hasDescription && (
            <Text id={descriptionId} variant="subheadline" tone="secondary">
              {description}
            </Text>
          )}
        </div>
        {children}
        {footer !== undefined && <div className={styles.actions}>{footer}</div>}
      </FocusScope>
    </div>
  );
}
